import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import ResearchLayout from '../../components/ResearchLayout';
import Pagination from '../../components/Pagination';
import api from '../../services/api';

const ViewDataPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dataType, setDataType] = useState('assessments');
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    pageSize: 10,
    totalItems: 0
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    scoreMin: '',
    scoreMax: '',
    dateFrom: '',
    dateTo: '',
    healthStatus: ''
  });
  const [sortConfig, setSortConfig] = useState({
    key: 'id',
    direction: 'asc'
  });
  const [exportLoading, setExportLoading] = useState(false);

  // Update dataTypes with correct API prefixes and endpoints
  const dataTypes = [
    { id: 'assessments', name: 'Health Assessments', icon: 'heartbeat', endpoint: '/reports/generate' },
    { id: 'protocols', name: 'Research Protocols', icon: 'clipboard-list', endpoint: '/protocols/assigned' }, // Use assigned protocols endpoint
    { id: 'answers', name: 'Protocol Answers', icon: 'comment-medical', endpoint: '/answers' }
  ];

  useEffect(() => {
    loadData();
  }, [dataType]);

  useEffect(() => {
    applyFiltersAndSearch();
  }, [data, searchTerm, filters]);

  // Enhanced loadData function to use the reports/generate endpoint for assessments
  const loadData = async () => {
    setLoading(true);
    try {
      const selectedType = dataTypes.find(type => type.id === dataType);
      console.log(`Fetching data for: ${dataType}`);
      
      // Special case for assessments - use the report generation endpoint
      if (dataType === 'assessments') {
        // Use the report generation endpoint which has complete demographic data
        const response = await api.post('/reports/generate', {
          report_type: 'statistical',
          filters: {
            dateRange: 'all', // Get all data
            // Add any other filters from your current filters state
            minScore: filters.scoreMin || undefined,
            maxScore: filters.scoreMax || undefined
          }
        });
        
        console.log(`Response from reports/generate:`, response.data);
        
        if (response.data && response.data.success) {
          // The assessment data is in the raw data that was used to generate the report
          const assessmentData = response.data.data.raw_assessments || [];
          
          // Log a sample to verify the structure
          if (assessmentData.length > 0) {
            console.log("Sample assessment with demographics:", assessmentData[0]);
          }
          
          setData(assessmentData);
          setFilteredData(assessmentData);
          
          setPagination({
            currentPage: 1,
            totalPages: Math.ceil(assessmentData.length / 10),
            pageSize: 10,
            totalItems: assessmentData.length
          });
          
          // Add to your loadData function for better debugging
          console.log("Sample AI assessment data:", assessmentData.slice(0, 3).map(a => {
            return {
              id: a.id,
              aiFields: {
                aiResponse: a.aiResponse,
                ai_response: a.ai_response, 
                ai_assessment: a.ai_assessment,
                aiAssessment: a.aiAssessment,
                analysis: a.analysis
              },
              gender: {
                gender_text: a.gender_text,
                genderText: a.genderText,
                gender: a.gender,
                demographics_gender: typeof a.demographics === 'object' ? a.demographics?.gender : 'not an object'
              }
            };
          }));
        }
      } else {
        // For other data types, use the regular endpoints with  prefix
        const endpoint = `/${dataType === 'protocols' ? 'protocols/assigned' : dataType}`;
        console.log(`Fetching from endpoint: ${endpoint}`);
        const response = await api.get(endpoint);
        console.log(`Response for ${dataType}:`, response.data);
        
        // Extract data based on endpoint structure
        let responseData = [];
        
        if (response.data && response.data.success) {
          if (dataType === 'protocols') {
            responseData = response.data.protocols || [];
            console.log(`Found ${responseData.length} assigned protocols`);
          } else if (dataType === 'answers') {
            responseData = response.data.answers || [];
          }
        }
        
        // Ensure responseData is always an array
        responseData = Array.isArray(responseData) ? responseData : [];
        
        setData(responseData);
        setFilteredData(responseData);
        
        // Ensure numeric values for pagination calculation
        const pageSize = 10;
        const totalItems = responseData.length;
        const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

        setPagination({
          currentPage: 1,
          totalPages: totalPages,
          pageSize: pageSize,
          totalItems: totalItems
        });
      }
    } catch (err) {
      console.error(`Failed to load ${dataType}`, err);
    } finally {
      setLoading(false);
    }
  };

  // Updated filter section for Health Status
  const applyFiltersAndSearch = () => {
    let result = [...data];
    
    // Apply search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(item => 
        // Different search fields based on data type
        dataType === 'assessments' ? 
          (item.customerId && String(item.customerId).toLowerCase().includes(term)) ||
          (item.aiResponse && item.aiResponse.toLowerCase().includes(term)) ||
          // Include mapped health status in search
          (item.physicalHealthPrediction && mapColorToHealthStatus(item.physicalHealthPrediction).toLowerCase().includes(term))
        : dataType === 'protocols' ?
          (item.name && item.name.toLowerCase().includes(term)) ||
          (item.description && item.description.toLowerCase().includes(term))
        : // answers
          (item.content && item.content.toLowerCase().includes(term))
      );
    }
    
    // Apply filters (mostly for assessments)
    if (dataType === 'assessments') {
      if (filters.scoreMin) {
        result = result.filter(item => item.score >= parseInt(filters.scoreMin));
      }
      
      if (filters.scoreMax) {
        result = result.filter(item => item.score <= parseInt(filters.scoreMax));
      }
      
      // Updated health status filter to use mapped color values
      if (filters.healthStatus) {
        result = result.filter(item => {
          if (!item.physicalHealthPrediction) return false;
          
          // Map the color code to a health status and then compare
          const mappedStatus = mapColorToHealthStatus(item.physicalHealthPrediction).toLowerCase();
          return mappedStatus === filters.healthStatus.toLowerCase();
        });
      }
      
      if (filters.dateFrom) {
        const fromDate = new Date(filters.dateFrom);
        result = result.filter(item => 
          item.created_at && new Date(item.created_at) >= fromDate
        );
      }
      
      if (filters.dateTo) {
        const toDate = new Date(filters.dateTo);
        toDate.setHours(23, 59, 59, 999); // End of day
        result = result.filter(item => 
          item.created_at && new Date(item.created_at) <= toDate
        );
      }
    }
    
    // Apply sorting
    result.sort((a, b) => {
      if (a[sortConfig.key] < b[sortConfig.key]) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (a[sortConfig.key] > b[sortConfig.key]) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
    
    setFilteredData(result);
    setPagination(prev => ({
      ...prev,
      totalPages: Math.ceil(result.length / prev.pageSize),
      totalItems: result.length,
      currentPage: 1 // Reset to first page when filtering
    }));
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const resetFilters = () => {
    setSearchTerm('');
    setFilters({
      scoreMin: '',
      scoreMax: '',
      dateFrom: '',
      dateTo: '',
      healthStatus: ''
    });
  };

  const exportData = async (format) => {
    setExportLoading(true);
    try {
      // Change from:
      // const response = await api.post(`/export/${dataType}`, {

      // To:
      const response = await api.post(`/export/${dataType}`, {
        format,
        filters: {
          ...filters,
          searchTerm
        }
      }, { responseType: 'blob' });
      
      // Create a download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${dataType}_export_${new Date().toISOString().split('T')[0]}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
    } catch (err) {
      console.error(`Failed to export ${dataType}`, err);
      alert(`Export failed. Please try again.`);
    } finally {
      setExportLoading(false);
    }
  };

  const handlePageChange = (page) => {
    setPagination(prev => ({
      ...prev,
      currentPage: page
    }));
  };

  const getCurrentPageData = () => {
    const startIndex = (pagination.currentPage - 1) * pagination.pageSize;
    const endIndex = startIndex + pagination.pageSize;
    return filteredData.slice(startIndex, endIndex);
  };

  const renderDataTable = () => {
    const currentData = getCurrentPageData();
    
    if (currentData.length === 0) {
      return (
        <div className="text-center py-5">
          <i className="fas fa-search fa-3x text-muted mb-3"></i>
          <h5 className="text-muted">No data found</h5>
          <p className="text-muted">Try adjusting your filters or search term</p>
          {(searchTerm || Object.values(filters).some(val => val)) && (
            <button className="btn btn-outline-secondary mt-2" onClick={resetFilters}>
              <i className="fas fa-undo me-2"></i>
              Reset Filters
            </button>
          )}
        </div>
      );
    }
    
    switch(dataType) {
      case 'assessments':
        return (
          <div className="table-responsive">
            <table className="table table-hover align-middle">
              <thead className="table-light">
                <tr>
                  <th onClick={() => handleSort('id')} className="sortable">
                    ID
                    {sortConfig.key === 'id' && (
                      <i className={`fas fa-sort-${sortConfig.direction === 'asc' ? 'up' : 'down'} ms-1`}></i>
                    )}
                  </th>
                  <th onClick={() => handleSort('customerId')} className="sortable">
                    Customer ID
                    {sortConfig.key === 'customerId' && (
                      <i className={`fas fa-sort-${sortConfig.direction === 'asc' ? 'up' : 'down'} ms-1`}></i>
                    )}
                  </th>
                  <th onClick={() => handleSort('score')} className="sortable">
                    Score
                    {sortConfig.key === 'score' && (
                      <i className={`fas fa-sort-${sortConfig.direction === 'asc' ? 'up' : 'down'} ms-1`}></i>
                    )}
                  </th>
                  <th onClick={() => handleSort('physicalHealthPrediction')} className="sortable">
                    Health Status
                    {sortConfig.key === 'physicalHealthPrediction' && (
                      <i className={`fas fa-sort-${sortConfig.direction === 'asc' ? 'up' : 'down'} ms-1`}></i>
                    )}
                  </th>
                  <th>Demographics</th>
                  <th>AI Assessment</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentData.map(assessment => (
                  <tr key={assessment.id}>
                    <td>{assessment.id}</td>
                    <td>{assessment.customerId}</td>
                    <td>
                      <span className={`badge ${
                        assessment.score >= 80 ? 'bg-success' :
                        assessment.score >= 60 ? 'bg-warning' :
                        'bg-danger'
                      }`}>
                        {assessment.score}/100
                      </span>
                    </td>
                    <td>
                      {(() => {
                        const healthStatus = mapColorToHealthStatus(assessment.physicalHealthPrediction);
                        const statusLower = healthStatus.toLowerCase();
                        
                        let statusClass = 'at-risk'; // Default fallback
                        if (statusLower === 'excellent') statusClass = 'excellent';
                        else if (statusLower === 'good') statusClass = 'good';
                        else if (statusLower === 'fair') statusClass = 'fair';
                        else if (statusLower === 'high risk') statusClass = 'high-risk';
                        else if (statusLower === 'critical') statusClass = 'critical';
                        
                        return (
                          <span className={`health-status ${statusClass}`}>
                            {healthStatus}
                          </span>
                        );
                      })()}
                    </td>
                    <td>
                      <small>
                        <div>
                          <strong>Age:</strong> {
                            assessment.age_group || 
                            assessment.ageGroup || 
                            assessment.age || 
                            (typeof assessment.demographics === 'object' && assessment.demographics?.age) || 
                            'N/A'
                          }
                        </div>
                        <div>
                          <strong>Gender:</strong> {
                            (() => {
                              // First check the numeric gender field since it's more reliable
                              const genderNumeric = assessment.gender;
                              
                              // If gender is explicitly 0 or 1, use that first
                              if (genderNumeric === 0) {
                                return 'Male';
                              }
                              if (genderNumeric === 1) {
                                return 'Female';
                              }
                              
                              // Then check other fields
                              const gender = assessment.genderText || 
                                             assessment.gender_text || 
                                             (typeof assessment.demographics === 'object' && assessment.demographics?.gender) ||
                                             '';
                              
                              // If no gender value found
                              if (!gender && gender !== 0) return 'N/A';
                              
                              // Convert to lowercase and trim for more accurate comparison if it's a string
                              const lowerGender = String(gender).toLowerCase().trim();
                              
                              // Check for all common variations of gender values
                              // Male variations
                              if (lowerGender === 'male' || lowerGender === 'm' || 
                                  lowerGender === 'man' || lowerGender === 'masculine') {
                                return 'Male';
                              } 
                              // Female variations
                              else if (lowerGender === 'female' || lowerGender === 'f' || 
                                       lowerGender === 'woman' || lowerGender === 'feminine') {
                                return 'Female';
                              } 
                              // Other variations
                              else if (lowerGender === 'other' || lowerGender === 'o' || lowerGender === '3' || 
                                       lowerGender === 'non-binary' || lowerGender === 'diverse') {
                                return 'Other';
                              }
                              
                              // Just return Male/Female based on numeric gender if it's the only clue we have
                              if (typeof assessment.gender === 'number') {
                                return assessment.gender === 0 ? 'Male' : 'Female';
                              }
                              
                              // Return Unknown only if we can't determine from any field
                              return 'Unknown';
                            })()
                          }
                        </div>
                      </small>
                    </td>
                    <td>
                      <div className="text-truncate" style={{ maxWidth: '200px' }}>
                        {assessment.aiResponse || 
                         assessment.ai_response || 
                         assessment.ai_assessment ||
                         assessment.aiAssessment ||
                         assessment.analysis || 
                         'No AI assessment available'}
                        {(assessment.aiResponse || assessment.ai_response || assessment.ai_assessment || 
                          assessment.aiAssessment || assessment.analysis) && '...'}
                      </div>
                    </td>
                    <td>
                      <button className="btn btn-sm btn-outline-info me-1">
                        <i className="fas fa-eye"></i>
                      </button>
                      <button className="btn btn-sm btn-outline-primary">
                        <i className="fas fa-chart-bar"></i>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
        
      case 'protocols':
        console.log('Rendering protocols table with data:', currentData);
        return (
          <div className="table-responsive">
            <table className="table table-hover align-middle">
              <thead className="table-light">
                <tr>
                  <th onClick={() => handleSort('id')} className="sortable">ID</th>
                  <th onClick={() => handleSort('title')} className="sortable">Title</th>
                  <th onClick={() => handleSort('questions_count')} className="sortable">Questions</th>
                  <th>Assignments</th>
                  <th onClick={() => handleSort('created_at')} className="sortable">Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentData.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="text-center py-4">
                      <p className="mb-0 text-muted">No protocols found</p>
                    </td>
                  </tr>
                ) : (
                  currentData.map(protocol => (
                    <tr key={protocol.id || `protocol-${Math.random()}`}>
                      <td>{protocol.id}</td>
                      <td>
                        <div className="d-flex flex-column">
                          <strong>{protocol.title || protocol.name || 'Untitled Protocol'}</strong>
                          {protocol.description && (
                            <small className="text-muted text-truncate" style={{ maxWidth: '300px' }}>
                              {protocol.description}
                            </small>
                          )}
                        </div>
                      </td>
                      <td>
                        <span className="badge bg-primary">
                          {protocol.questions_count || (protocol.questions && protocol.questions.length) || 0}
                        </span>
                      </td>
                      <td>
                        {protocol.assignments && protocol.assignments.length > 0 ? (
                          <div>
                            {protocol.assignments.map((assignment, idx) => (
                              <div key={`assignment-${protocol.id}-${idx}`} className="mb-1">
                                <small>
                                  <span className={`badge ${assignment.completed ? 'bg-success' : 'bg-warning'}`}>
                                    {assignment.completed ? 'Completed' : 'In Progress'} ({assignment.progress})
                                  </span>
                                  <span className="ms-1">{assignment.user.first_name} {assignment.user.last_name}</span>
                                </small>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <span className="badge bg-secondary">Not Assigned</span>
                        )}
                      </td>
                      <td>
                        {protocol.created_at ? 
                          new Date(protocol.created_at).toLocaleDateString() : 'N/A'}
                      </td>
                      <td>
                        <div className="d-flex">
                          <button className="btn btn-sm btn-outline-info me-1" title="View Protocol">
                            <i className="fas fa-eye"></i>
                          </button>
                          <button className="btn btn-sm btn-outline-primary" title="View Results">
                            <i className="fas fa-chart-bar"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        );
        
      case 'answers':
        return (
          <div className="table-responsive">
            <table className="table table-hover align-middle">
              <thead className="table-light">
                <tr>
                  <th onClick={() => handleSort('id')} className="sortable">
                    ID
                    {sortConfig.key === 'id' && (
                      <i className={`fas fa-sort-${sortConfig.direction === 'asc' ? 'up' : 'down'} ms-1`}></i>
                    )}
                  </th>
                  <th onClick={() => handleSort('answered_at')} className="sortable">
                    Answered At
                    {sortConfig.key === 'answered_at' && (
                      <i className={`fas fa-sort-${sortConfig.direction === 'asc' ? 'up' : 'down'} ms-1`}></i>
                    )}
                  </th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentData.map(answer => (
                  <tr key={answer.id}>
                    <td>{answer.id}</td>
                    <td>
                      <div className="text-truncate" style={{ maxWidth: '300px' }}>
                        {answer.question_text || (answer.question && answer.question.text) || `Question #${answer.question_id}` || 'N/A'}
                      </div>
                    </td>
                    <td>
                      <div className="text-truncate" style={{ maxWidth: '300px' }}>
                        {answer.content || answer.answer_text || answer.answer || answer.value || 'N/A'}
                      </div>
                    </td>
                    <td>{answer.case_id}</td>
                    <td>
                      {answer.answered_at ? 
                        new Date(answer.answered_at).toLocaleDateString() : 'N/A'}
                    </td>
                    <td>
                      <button className="btn btn-sm btn-outline-info">
                        <i className="fas fa-eye"></i>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
        
      default:
        return <div>No data available</div>;
    }
  };

  // Add this function at the component level, before the return statement
  const mapColorToHealthStatus = (colorCode) => {
    if (!colorCode) return 'Unknown';
    
    const colorMap = {
      "#83D65C": "Excellent",
      "#5BC0DE": "Good",
      "#ffe366": "Good",
      "#ff944d": "Fair", 
      "#ff6666": "High Risk",
      "#d9534f": "Critical"
    };
    
    // Try to match the color code exactly
    return colorMap[colorCode] || 'Unknown';
  };

  return (
    <ResearchLayout pageTitle="View All Data">
      <div className="container-fluid">
        <div className="row mb-4">
          <div className="col">
            <div className="card border-0 shadow-sm bg-gradient-info text-white">
              <div className="card-body" style={{backgroundColor: '#7B68EE'}}>
                <h3 className="mb-2">Research Data Explorer</h3>
                <p className="mb-0 opacity-75">
                  Explore, filter, and export all your research data
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Data Type Selector */}
        <div className="row mb-4">
          <div className="col">
            <div className="card border-0 shadow-sm">
              <div className="card-body p-0">
                <ul className="nav nav-tabs data-types">
                  {dataTypes.map(type => (
                    <li className="nav-item" key={type.id}>
                      <button 
                        className={`nav-link ${dataType === type.id ? 'active' : ''}`}
                        onClick={() => setDataType(type.id)}
                      >
                        <i className={`fas fa-${type.icon} me-2`}></i>
                        {type.name}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Filters and Data Table */}
        <div className="row">
          <div className="col-12">
            <div className="card border-0 shadow-sm mb-4">
              {/* Search and Filter Header */}
              <div className="card-header bg-white">
                <div className="row align-items-center">
                  <div className="col-lg-6 mb-3 mb-lg-0">
                    <div className="input-group">
                      <span className="input-group-text bg-transparent border-end-0">
                        <i className="fas fa-search text-muted"></i>
                      </span>
                      <input
                        type="text"
                        className="form-control border-start-0"
                        placeholder="Search..."
                        value={searchTerm}
                        onChange={handleSearch}
                      />
                    </div>
                  </div>
                  <div className="col-lg-6">
                    <div className="d-flex justify-content-lg-end">
                      <button className="btn btn-outline-secondary me-2" type="button" data-bs-toggle="collapse" data-bs-target="#filterCollapse">
                        <i className="fas fa-filter me-2"></i>
                        Filters
                      </button>
                      <div className="dropdown">
                        <button className="btn btn-info text-white dropdown-toggle" type="button" id="exportDropdown" data-bs-toggle="dropdown" aria-expanded="false" disabled={exportLoading || filteredData.length === 0}>
                          {exportLoading ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                              Exporting...
                            </>
                          ) : (
                            <>
                              <i className="fas fa-download me-2"></i>
                              Export
                            </>
                          )}
                        </button>
                        <ul className="dropdown-menu" aria-labelledby="exportDropdown">
                          <li><button className="dropdown-item" onClick={() => exportData('csv')}>CSV</button></li>
                          <li><button className="dropdown-item" onClick={() => exportData('xlsx')}>Excel</button></li>
                          <li><button className="dropdown-item" onClick={() => exportData('json')}>JSON</button></li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Collapsible Filter Section */}
                <div className="collapse mt-3" id="filterCollapse">
                  <div className="card card-body border">
                    <div className="row">
                      {dataType === 'assessments' && (
                        <>
                          <div className="col-md-3 mb-3">
                            <label className="form-label">Min Score</label>
                            <input
                              type="number"
                              className="form-control"
                              name="scoreMin"
                              value={filters.scoreMin}
                              onChange={handleFilterChange}
                              placeholder="Min"
                            />
                          </div>
                          <div className="col-md-3 mb-3">
                            <label className="form-label">Max Score</label>
                            <input
                              type="number"
                              className="form-control"
                              name="scoreMax"
                              value={filters.scoreMax}
                              onChange={handleFilterChange}
                              placeholder="Max"
                            />
                          </div>
                          <div className="col-md-3 mb-3">
                            <label className="form-label">Health Status</label>
                            <select
                              className="form-select"
                              name="healthStatus"
                              value={filters.healthStatus}
                              onChange={handleFilterChange}
                            >
                              <option value="">All</option>
                              <option value="excellent">Excellent</option>
                              <option value="good">Good</option>
                              <option value="fair">Fair</option>
                              <option value="high risk">High Risk</option>
                              <option value="critical">Critical</option>
                            </select>
                          </div>
                        </>
                      )}
                      <div className="col-md-3 mb-3">
                        <label className="form-label">From Date</label>
                        <input
                          type="date"
                          className="form-control"
                          name="dateFrom"
                          value={filters.dateFrom}
                          onChange={handleFilterChange}
                        />
                      </div>
                      <div className="col-md-3 mb-3">
                        <label className="form-label">To Date</label>
                        <input
                          type="date"
                          className="form-control"
                          name="dateTo"
                          value={filters.dateTo}
                          onChange={handleFilterChange}
                        />
                      </div>
                      <div className="col-md-12">
                        <button className="btn btn-secondary" onClick={resetFilters}>
                          <i className="fas fa-undo me-2"></i>
                          Reset Filters
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Data Table */}
              <div className="card-body p-0">
                {loading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ) : (
                  renderDataTable()
                )}
              </div>
              
              {/* Pagination */}
              {!loading && filteredData.length > 0 && (
                <div className="card-footer bg-white">
                  <Pagination
                    currentPage={pagination.currentPage}
                    totalPages={pagination.totalPages}
                    onPageChange={handlePageChange}
                    showInfo={true}
                    totalItems={filteredData.length}
                    itemsPerPage={pagination.pageSize}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      <style jsx>{`
        .health-status {
          display: inline-block;
          padding: 4px 8px;
          border-radius: 4px;
          font-weight: 500;
        }
        
        .health-status.excellent {
          background-color: rgba(40, 167, 69, 0.2);
          color: #28a745;
        }
        
        .health-status.good {
          background-color: rgba(23, 162, 184, 0.2);
          color: #17a2b8;
        }
        
        .health-status.fair {
          background-color: rgba(255, 193, 7, 0.2);
          color: #e6a800;
        }
        
        .health-status.at-risk,
        .health-status.high-risk {
          background-color: rgba(220, 53, 69, 0.2);
          color: #dc3545;
        }
        
        .health-status.critical {
          background-color: rgba(153, 0, 0, 0.2);
          color: #990000;
        }
        
        .sortable {
          cursor: pointer;
        }
        
        .sortable:hover {
          background-color: #f8f9fa;
        }
        
        .data-types .nav-link {
          padding: 1rem 1.5rem;
          color: #6c757d;
          border: none;
          border-bottom: 3px solid transparent;
        }
        
        .data-types .nav-link.active {
          color: #17a2b8;
          border-bottom: 3px solid #17a2b8;
          font-weight: 500;
        }
        
        .data-types .nav-link:hover:not(.active) {
          border-bottom: 3px solid #e9ecef;
        }
      `}</style>
    </ResearchLayout>
  );
};

export default ViewDataPage;